

const MAX_BREADCRUMBS = 5;

const IGNORE_CLICK_TAGS = new Set(['html', 'body']);

let _crumbs = [];
let _initialized = false;
let _teardownFns = [];

function addBreadcrumb(crumb) {
  _crumbs.push({
    category: crumb.category || 'custom',
    action: crumb.action || '',
    message: crumb.message || '',
    data: crumb.data || null,
    timestamp: new Date().toISOString(),
    level: crumb.level || 'info',
  });
  if (_crumbs.length > MAX_BREADCRUMBS) {
    _crumbs = _crumbs.slice(-MAX_BREADCRUMBS);
  }
}

export function getBreadcrumbs() {
  return [..._crumbs];
}

function trunc(str, max = 120) {
  if (!str) return '';
  return str.length > max ? str.slice(0, max) + '…' : str;
}

function getLabel(el) {
  if (!el || !el.tagName) return '';
  const tag = el.tagName.toLowerCase();

  const ariaLabel = el.getAttribute('aria-label');
  if (ariaLabel) return trunc(ariaLabel, 60);

  const title = el.getAttribute('title');
  if (title) return trunc(title, 60);

  if (tag === 'input' || tag === 'textarea' || tag === 'select') {
    return findFieldLabel(el);
  }

  const text = getDirectText(el);
  if (text) return trunc(text, 60);

  const testId = el.getAttribute('data-testid');
  if (testId) return testId;

  if (el.id && el.id.length < 30 && !el.id.startsWith('radix-')) return el.id;

  return '';
}

function getDirectText(el) {
  const text = (el.innerText || el.textContent || '').trim();
  if (!text) return '';
  if (text.length <= 60) return text;

  const firstLine = text.split('\n')[0].trim();
  if (firstLine && firstLine.length <= 60) return firstLine;

  return '';
}

function findFieldLabel(el) {
  const ariaLabel = el.getAttribute('aria-label');
  if (ariaLabel) return trunc(ariaLabel, 60);

  if (el.id) {
    const label = document.querySelector(`label[for="${el.id}"]`);
    if (label) return trunc(label.textContent, 60);
  }

  const parentLabel = el.closest('label');
  if (parentLabel) return trunc(parentLabel.textContent, 60);

  const placeholder = el.getAttribute('placeholder');
  if (placeholder) return trunc(placeholder, 60);

  return el.getAttribute('name') || '';
}

function getElementType(el) {
  const tag = el.tagName.toLowerCase();
  const role = el.getAttribute('role');
  const type = el.getAttribute('type');

  if (role === 'menuitem') return 'menu item';
  if (role === 'option') return 'option';
  if (role === 'tab') return 'tab';
  if (role === 'checkbox') return 'checkbox';
  if (role === 'radio') return 'radio';
  if (role === 'switch') return 'switch';
  if (role === 'slider') return 'slider';
  if (role === 'combobox') return 'combobox';
  if (role === 'dialog') return 'dialog';
  if (role === 'menu') return 'menu';
  if (role === 'listbox') return 'listbox';
  if (role === 'treeitem') return 'tree item';
  if (role === 'button' || tag === 'button') return 'button';
  if (role === 'link' || tag === 'a') return 'link';

  if (tag === 'input') return type ? `${type} input` : 'input';
  if (tag === 'select') return 'dropdown';
  if (tag === 'textarea') return 'text area';
  if (tag === 'img') return 'image';
  if (tag === 'svg' || el.closest('svg')) return 'icon';
  if (tag === 'td' || tag === 'th') return 'table cell';
  if (tag === 'tr') return 'table row';
  if (tag === 'li') return 'list item';
  if (tag === 'label') return 'label';
  if (tag === 'h1' || tag === 'h2' || tag === 'h3' || tag === 'h4') return 'heading';
  if (tag === 'span' || tag === 'div' || tag === 'p') return 'element';

  return tag;
}

function handleClick(event) {
  const el = event.target;
  if (!el || !el.tagName) return;

  const tag = el.tagName.toLowerCase();

  if (IGNORE_CLICK_TAGS.has(tag)) return;

  const clickTarget = (tag === 'path' || tag === 'circle' || tag === 'line' || tag === 'rect')
    ? el.closest('svg')?.parentElement || el
    : el;

  const label = getLabel(clickTarget);
  const elementType = getElementType(clickTarget);

  const message = label
    ? `Clicked «${label}»`
    : `Clicked ${elementType}`;

  addBreadcrumb({
    category: 'ui.click',
    action: 'click',
    message,
    level: 'info',
  });
}

function handleChange(event) {
  const el = event.target;
  if (!el || !el.tagName) return;

  const tag = el.tagName.toLowerCase();
  if (tag !== 'input' && tag !== 'select' && tag !== 'textarea') return;

  const fieldType = el.getAttribute('type') || tag;
  const label = findFieldLabel(el);

  const message = label
    ? `Changed «${label}» ${fieldType} field`
    : `Changed ${fieldType} field`;

  addBreadcrumb({
    category: 'ui.input',
    action: 'change',
    message,
    level: 'info',
  });
}

let _lastUrl = '';

function trackNavigation() {
  const url = window.location.href;
  if (url === _lastUrl) return;

  const from = _lastUrl;
  _lastUrl = url;

  if (!from) return;

  addBreadcrumb({
    category: 'navigation',
    action: 'navigate',
    message: `Navigated to ${window.location.pathname}`,
    data: {
      from: new URL(from).pathname,
      to: window.location.pathname,
    },
    level: 'info',
  });
}

let _originalFetch = null;

function installFetchInterceptor() {
  _originalFetch = window.fetch;

  window.fetch = async function patchedFetch(input, init) {
    const url = typeof input === 'string' ? input : input?.url || String(input);
    const method = (init?.method || 'GET').toUpperCase();

    if (url.includes('/api/ingest')) {
      return _originalFetch.call(this, input, init);
    }

    const startTime = Date.now();

    try {
      const response = await _originalFetch.call(this, input, init);
      const duration = Date.now() - startTime;

      const parsedUrl = safeParseUrl(url);
      const path = parsedUrl ? parsedUrl.pathname : url;

      const level = response.status >= 400 ? 'error' : 'info';
      addBreadcrumb({
        category: 'http',
        action: `${method} ${response.status}`,
        message: `${method} ${trunc(path, 80)} → ${response.status} (${duration}ms)`,
        data: { method, url: trunc(path, 200), status: response.status, duration },
        level,
      });

      return response;
    } catch (err) {
      const duration = Date.now() - startTime;
      addBreadcrumb({
        category: 'http',
        action: `${method} FAILED`,
        message: `${method} ${trunc(url, 80)} failed: ${err.message} (${duration}ms)`,
        data: { method, url: trunc(url, 200), error: err.message, duration },
        level: 'error',
      });
      throw err;
    }
  };
}

function safeParseUrl(url) {
  try { return new URL(url, window.location.origin); }
  catch { return null; }
}

let _originalConsoleWarn = null;
let _originalConsoleError = null;

function installConsoleInterceptors() {
  _originalConsoleWarn = console.warn;
  _originalConsoleError = console.error;

  console.warn = function (...args) {
    const first = args[0];

    if (typeof first === 'string' && (
      first.includes('React Router Future Flag Warning') ||
      first.includes('ReactDOM.render is no longer supported') ||
      first.startsWith('[Microscope')
    )) {
      return _originalConsoleWarn.apply(console, args);
    }

    addBreadcrumb({
      category: 'console',
      action: 'warn',
      message: trunc(formatConsoleArgs(args), 200),
      level: 'warn',
    });
    return _originalConsoleWarn.apply(console, args);
  };

  console.error = function (...args) {
    const first = args[0];
    if (typeof first === 'string' && (
      first.includes('The above error occurred') ||
      first.startsWith('[Microscope')
    )) {
      return _originalConsoleError.apply(console, args);
    }

    addBreadcrumb({
      category: 'console',
      action: 'error',
      message: trunc(formatConsoleArgs(args), 200),
      level: 'error',
    });
    return _originalConsoleError.apply(console, args);
  };
}

function formatConsoleArgs(args) {
  return args
    .map((arg) => {
      if (typeof arg === 'string') return arg;
      if (arg instanceof Error) return arg.message;
      try { return JSON.stringify(arg); } catch { return String(arg); }
    })
    .join(' ');
}

function handleVisibilityChange() {
  const state = document.visibilityState;
  addBreadcrumb({
    category: 'app',
    action: state === 'visible' ? 'focus' : 'blur',
    message: state === 'visible' ? 'Tab became visible' : 'Tab became hidden',
    level: 'info',
  });
}

export function initBreadcrumbs() {
  if (_initialized) return;
  _initialized = true;
  _lastUrl = window.location.href;

  document.addEventListener('click', handleClick, true);
  _teardownFns.push(() => document.removeEventListener('click', handleClick, true));

  document.addEventListener('change', handleChange, true);
  _teardownFns.push(() => document.removeEventListener('change', handleChange, true));

  const origPushState = history.pushState;
  const origReplaceState = history.replaceState;
  history.pushState = function (...args) { origPushState.apply(this, args); trackNavigation(); };
  history.replaceState = function (...args) { origReplaceState.apply(this, args); trackNavigation(); };
  window.addEventListener('popstate', trackNavigation);
  _teardownFns.push(() => {
    history.pushState = origPushState;
    history.replaceState = origReplaceState;
    window.removeEventListener('popstate', trackNavigation);
  });

  installFetchInterceptor();
  _teardownFns.push(() => { if (_originalFetch) { window.fetch = _originalFetch; _originalFetch = null; } });

  installConsoleInterceptors();
  _teardownFns.push(() => {
    if (_originalConsoleWarn) { console.warn = _originalConsoleWarn; _originalConsoleWarn = null; }
    if (_originalConsoleError) { console.error = _originalConsoleError; _originalConsoleError = null; }
  });

  document.addEventListener('visibilitychange', handleVisibilityChange);
  _teardownFns.push(() => document.removeEventListener('visibilitychange', handleVisibilityChange));

  addBreadcrumb({
    category: 'app',
    action: 'init',
    message: `App loaded at ${window.location.pathname}`,
    level: 'info',
  });
}

export function teardownBreadcrumbs() {
  _teardownFns.forEach((fn) => fn());
  _teardownFns = [];
  _crumbs = [];
  _initialized = false;
}
